import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiBusSide, mdiCarHatchback, mdiMotorbike, mdiVanPassenger } from "@mdi/js";
import { Box } from "@mui/system";

import CircularLoader from "components/CircularLoader";
import { Vehicle, VehicleType } from "api/vote";

export interface GoogleCalendarDialogProps {
  onClose: () => void;
  onConfirm: (type: VehicleType, availableSeats: number) => Promise<void>;
  onRemove: () => Promise<void>;
  open: boolean;
  vehicle?: Vehicle | null;
}

function valuetext(value: number) {
  return `${value} seats`;
}

export const VEHICLES = {
  motorcycle: { icon: mdiMotorbike, seats: 2 },
  "mid-size-car": { icon: mdiCarHatchback, seats: 7 },
  van: { icon: mdiVanPassenger, seats: 13 },
  bus: { icon: mdiBusSide, seats: 60 },
};

function VehicleSelectionDialog(props: GoogleCalendarDialogProps) {
  const { onClose, onConfirm, onRemove, open, vehicle } = props;
  const [isLoading, setIsLoading] = useState<"confirm" | "remove" | null>(null);
  const [vehicleType, setVehicleType] = useState<VehicleType>(VehicleType.MID_SIZE_CAR);
  const [availableSeats, setavailableSeats] = useState(5);

  useEffect(() => {
    if (vehicle) {
      setVehicleType(vehicle.type);
      setavailableSeats(vehicle.availableSeats);
    } else {
      setVehicleType(VehicleType.MID_SIZE_CAR);
      setavailableSeats(5);
    }

    if (open) {
      setIsLoading(null);
    }
  }, [vehicle, open]);

  const handleChangeVehicleType = (event: React.MouseEvent<HTMLElement>, newVehicleType: VehicleType) => {
    setVehicleType(newVehicleType);

    switch (newVehicleType) {
      case VehicleType.MOTORCYCLE:
        setavailableSeats(VEHICLES.motorcycle.seats);
        break;
      case VehicleType.VAN:
        setavailableSeats(VEHICLES.van.seats);
        break;
      case VehicleType.BUS:
        setavailableSeats(VEHICLES.bus.seats);
        break;
      default:
        setavailableSeats(VEHICLES["mid-size-car"].seats);
        break;
    }
  };

  const handleSeatsChange = (event: globalThis.Event, value: number | number[]) => {
    if (typeof value === "number") {
      setavailableSeats(value);
    }
  };

  const handleConfirm = async () => {
    setIsLoading("confirm");
    await onConfirm(vehicleType, availableSeats);
  };

  const handleRemove = async () => {
    setIsLoading("remove");
    await onRemove();
  };

  const renderVehicleType = () => (
    <Box mt={3}>
      <Typography mb={2} color='text.secondary'>
        Select vehicle type
      </Typography>
      <ToggleButtonGroup
        aria-label='Select vehicle'
        color='primary'
        exclusive
        fullWidth
        onChange={handleChangeVehicleType}
        value={vehicleType}
      >
        <ToggleButton aria-label='motorcycle' value={VehicleType.MOTORCYCLE}>
          <Icon path={VEHICLES.motorcycle.icon} size={1} />
        </ToggleButton>
        <ToggleButton aria-label='mid-size-car' value={VehicleType.MID_SIZE_CAR}>
          <Icon path={VEHICLES["mid-size-car"].icon} size={1} />
        </ToggleButton>
        <ToggleButton aria-label='van' value={VehicleType.VAN}>
          <Icon path={VEHICLES.van.icon} size={1} />
        </ToggleButton>
        <ToggleButton aria-label='bus' value={VehicleType.BUS}>
          <Icon path={VEHICLES.bus.icon} size={1} />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderSeatsSlider = () => {
    // Determine [max] value
    let max: number | undefined;
    const marks: Array<{ value: number; label: string }> = [];

    // Generate [marks] based on [max] value
    switch (vehicleType) {
      case "motorcycle":
        max = VEHICLES.motorcycle.seats;
        for (let i = 1; i <= max; i += 1) {
          marks.push({ label: i.toString(), value: i });
        }
        break;
      case "van":
        max = VEHICLES.van.seats;
        marks.push({ label: "1", value: 1 });
        for (let i = 3; i <= max; i += 2) {
          marks.push({ label: i.toString(), value: i });
        }
        break;
      case "bus":
        max = VEHICLES.bus.seats;
        marks.push({ label: "1", value: 1 });
        for (let i = 10; i <= max; i += 10) {
          marks.push({ label: i.toString(), value: i });
        }
        break;
      default:
        max = VEHICLES["mid-size-car"].seats;
        for (let i = 1; i <= max; i += 1) {
          marks.push({ label: i.toString(), value: i });
        }
        break;
    }

    return (
      <Box mt={3}>
        <Typography color='text.secondary' gutterBottom>
          Available vehicle seats (including yours)
        </Typography>
        <Box px={1}>
          <Slider
            data-testid='seats-slider'
            aria-label='Available vehicle seats (including yours)'
            getAriaValueText={valuetext}
            marks={marks}
            max={max}
            min={1}
            onChange={handleSeatsChange}
            step={1}
            value={availableSeats}
            valueLabelDisplay='auto'
          />
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='vehicle-selection' maxWidth='xs' fullWidth>
      <DialogTitle id='vehicle-selection'>Vehicle selection</DialogTitle>
      <DialogContent>
        <Typography>
          If you participate to this event with your motorcycle, car or any other vehicle, you can specify the available
          number of seats you have.
        </Typography>
        {renderVehicleType()}
        {renderSeatsSlider()}
      </DialogContent>
      <DialogActions>
        <CircularLoader loading={isLoading === "remove"}>
          {Boolean(vehicle) && (
            <Button disabled={Boolean(isLoading)} onClick={handleRemove}>
              Remove
            </Button>
          )}
        </CircularLoader>
        {!vehicle && (
          <Button disabled={Boolean(isLoading)} onClick={onClose}>
            Cancel
          </Button>
        )}
        <CircularLoader loading={isLoading === "confirm"}>
          <Button disabled={Boolean(isLoading)} onClick={handleConfirm}>
            Confirm
          </Button>
        </CircularLoader>
      </DialogActions>
    </Dialog>
  );
}

export default VehicleSelectionDialog;
